// src/components/Platform/CommunicationsCenter/MessageCard.js

import React from "react";
import styled, { css } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPen,
  faTrash,
  faClock,
  faCheckCircle,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";

const typeStyles = {
  AVISO: {
    icon: "📢",
    bgColor: "#fffbeb",
    borderColor: "#fde68a",
    titleColor: "#b45309",
  },
  PROMOCAO: {
    icon: "🔥",
    bgColor: "#fef2f2",
    borderColor: "#fecaca",
    titleColor: "#b91c1c",
  },
  ATUALIZACAO: {
    icon: "⚙️",
    bgColor: "#eff6ff",
    borderColor: "#bfdbfe",
    titleColor: "#1d4ed8",
  },
  NOTICIA: {
    icon: "📰",
    bgColor: "#f0fdf4",
    borderColor: "#bbf7d0",
    titleColor: "#15803d",
  },
};

const Card = styled.div`
  background-color: ${(props) => props.styles.bgColor};
  border: 1px solid ${(props) => props.styles.borderColor};
  border-left: 5px solid ${(props) => props.styles.borderColor};
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease-in-out;
  cursor: ${(props) => (!props.isPreview ? "pointer" : "default")};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:hover {
    transform: ${(props) => (!props.isPreview ? "translateY(-2px)" : "none")};
    box-shadow: ${(props) =>
      !props.isPreview ? "0 4px 15px rgba(0,0,0,0.07)" : "none"};
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const Title = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  gap: 8px;
  word-break: break-word;
`;

const StatusBadge = styled.span`
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 12px;
  text-transform: uppercase;
  flex-shrink: 0;
  margin-left: 8px;

  ${(props) => {
    switch (props.status) {
      case "SENT":
        return css`
          background-color: #dcfce7;
          color: #166534;
        `;
      case "SCHEDULED":
        return css`
          background-color: #fef9c3;
          color: #854d0e;
        `;
      case "DRAFT":
        return css`
          background-color: #e5e7eb;
          color: #4b5563;
        `;
      default:
        return ``;
    }
  }}
`;

const CardBody = styled.p`
  font-size: 0.9rem;
  color: #4b5563;
  margin: 0 0 16px 0;
  line-height: 1.5;
  word-break: break-word;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto; /* Empurra o rodapé para baixo */
`;

const Link = styled.a`
  font-size: 0.875rem;
  font-weight: 600;
  color: #3b82f6;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  padding: 4px;
  font-size: 0.9rem;
  &:hover {
    color: ${(props) => (props.danger ? "#ef4444" : "#334155")};
  }
`;

const InfoText = styled.span`
  font-size: 0.8rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
`;

export default function MessageCard({
  message,
  onEdit,
  onDelete,
  isPreview = false,
}) {
  const styles = typeStyles[message.messageType] || typeStyles.AVISO;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Card
      styles={styles}
      isPreview={isPreview}
      onClick={!isPreview ? onEdit : undefined}
    >
      <div>
        <CardHeader>
          <Title color={styles.titleColor}>
            <span>{styles.icon}</span> {message.title || "Sem Título"}
          </Title>
          {!isPreview && (
            <StatusBadge status={message.status}>{message.status}</StatusBadge>
          )}
        </CardHeader>
        <CardBody>{message.text || "Sem conteúdo..."}</CardBody>
      </div>

      <div>
        <CardFooter>
          {message.link ? (
            <Link
              href={message.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Ver Link
            </Link>
          ) : (
            <div />
          )}

          {!isPreview && (
            <Actions>
              <ActionButton
                title="Editar"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <FontAwesomeIcon icon={faPen} />
              </ActionButton>
              <ActionButton
                title="Excluir"
                danger
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </ActionButton>
            </Actions>
          )}
        </CardFooter>

        {!isPreview && message.createdByAdminName && (
          <InfoText>
            <FontAwesomeIcon icon={faUserShield} /> Criado por:{" "}
            {message.createdByAdminName}
          </InfoText>
        )}
        {message.status === "SENT" && (
          <InfoText>
            <FontAwesomeIcon icon={faCheckCircle} /> Enviada em:{" "}
            {formatDate(message.sentAt)}
          </InfoText>
        )}
        {message.status === "SCHEDULED" && (
          <InfoText>
            <FontAwesomeIcon icon={faClock} /> Agendada para:{" "}
            {formatDate(message.scheduledFor)}
          </InfoText>
        )}
      </div>
    </Card>
  );
}
